import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Switch,
  Space,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Collapse,
  Table,
  Spin,
  Empty,
  Upload,
  Avatar,
  Slider,
  Divider,
  Typography,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SettingOutlined,
  UploadOutlined,
  HolderOutlined,
  DollarOutlined,
  StarFilled,
  StarOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getModelListImageUnifiedV2, getUnifiedV2MinVersion, getCatalogueStandards } from '../../graphql/queries';
import {
  updateCreditConfigImageUnifiedV2,
  getSignedUrl,
  getObjectUrl,
  updateUnifiedV2MinVersion,
} from '../../graphql/mutation';
import { uploadToR2 } from '../../utils/uploadToR2';

const { Title } = Typography;

const SortableModelWrapper = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'stretch',
        marginBottom: 16,
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#bfbfbf',
          background: '#fafafa',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        <HolderOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

const EMPTY_MODEL_DIALOG = {
  visible: false,
  modelId: '',
  modelName: '',
  badge: '',
  icon: '',
  iconFile: null,
  costPreview: 1,
  maxCharacterLimit: null,
  modelDescription: '',
  isDefault: false,
  isFreeCropImage: false,
  hasResolutionTiers: false,
  resolutionRows: [], // [{ key, value, name, actualCost }]
  actualCost: 0, // used when hasResolutionTiers is false — flat $/image on the model root
};

// Standard-driven chips: clicking an inactive chip adds a row prefilled from that
// standard (key/name/short_name/...); clicking an already-added (highlighted) chip
// removes it — a toggle, mirroring the row-level add/remove actions. Only keys not
// already present on this list are offered.
const StandardChips = ({ standards, existingKeys, onAdd, onRemove, color = '#1677ff' }) => (
  <Space wrap size={8} style={{ marginBottom: 8 }}>
    {(standards || []).map((s) => {
      const isActive = existingKeys.has(s.key);
      return (
        <Tooltip key={s.key} title={s.label}>
          <Button
            size="small"
            onClick={() => (isActive ? onRemove(s.key) : onAdd(s))}
            style={isActive ? { borderColor: color, color, background: `${color}14` } : {}}
          >
            {s.key}
          </Button>
        </Tooltip>
      );
    })}
  </Space>
);

// Fallback for legacy rows saved before the standards catalog existed — lets an
// admin backfill a `key` on a row that doesn't have one yet, so the next save
// doesn't get rejected with INVALID_STANDARD_KEY.
const LegacyKeyPicker = ({ standards, onPick }) => (
  <Space size={4}>
    <Tag icon={<WarningOutlined />} color="warning">Not linked to a standard</Tag>
    <Select
      size="small"
      placeholder="Pick a key"
      style={{ width: 140 }}
      options={(standards || []).map((s) => ({ value: s.key, label: s.label }))}
      onChange={onPick}
    />
  </Space>
);

// Free-form ratio list editor (used for cropRatios / textToImageRatios / imgToImageRatios).
// Rows are keyed off the shared ratio standards catalog — `key`/`name`/`short_name`
// (and, for crop ratios, aspect_ratio_x/y) come from the standard and are read-only;
// only `value` (the provider-specific raw parameter) is editable per model.
const RatioListEditor = ({ title, rows, onChange, defaultValue, onSetDefault, withAspectXY = false, standardsList }) => {
  const existingKeys = new Set((rows || []).map((r) => r.key).filter(Boolean));

  const rowFromStandard = (s, previousValue) =>
    withAspectXY
      ? { key: s.key, value: previousValue ?? s.key, name: s.label, short_name: s.short_name, aspect_ratio_x: s.aspect_ratio_x ?? null, aspect_ratio_y: s.aspect_ratio_y ?? null }
      : { key: s.key, value: previousValue ?? s.key, name: s.label, short_name: s.short_name };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>
        {title}
      </div>
      <StandardChips
        standards={standardsList}
        existingKeys={existingKeys}
        onAdd={(s) => onChange([...(rows || []), rowFromStandard(s)])}
        onRemove={(key) => onChange((rows || []).filter((r) => r.key !== key))}
      />
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {(rows || []).map((row, i) => (
          <Space key={i} style={{ width: '100%' }}>
            <Tooltip title={defaultValue != null && row.key === defaultValue ? 'Default' : 'Set as default'}>
              <Button
                type="text"
                size="small"
                icon={defaultValue != null && row.key === defaultValue ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                onClick={() => onSetDefault && onSetDefault(row.key)}
              />
            </Tooltip>
            {row.key ? (
              <Tag style={{ minWidth: 60, textAlign: 'center' }}>{row.key}</Tag>
            ) : (
              <LegacyKeyPicker
                standards={(standardsList || []).filter((s) => !existingKeys.has(s.key))}
                onPick={(key) => {
                  const s = (standardsList || []).find((x) => x.key === key);
                  if (!s) return;
                  const next = JSON.parse(JSON.stringify(rows));
                  next[i] = rowFromStandard(s, row.value);
                  onChange(next);
                }}
              />
            )}
            <Input
              size="small"
              placeholder="value"
              title="value (provider-specific raw parameter)"
              style={{ width: 120 }}
              value={row.value}
              onChange={(e) => {
                const next = JSON.parse(JSON.stringify(rows));
                next[i].value = e.target.value;
                onChange(next);
              }}
            />
            {withAspectXY ? (
              <span style={{ width: 60, fontSize: 12, color: '#999' }}>
                {row.aspect_ratio_x != null && row.aspect_ratio_y != null ? `${row.aspect_ratio_x}x${row.aspect_ratio_y}` : '—'}
              </span>
            ) : (
              <>
                <span style={{ width: 140, fontSize: 12, color: '#666' }}>{row.name}</span>
                <span style={{ width: 80, fontSize: 12, color: '#999' }}>{row.short_name}</span>
              </>
            )}
          </Space>
        ))}
      </Space>
    </div>
  );
};

export const ImageUnifiedCreditConfigV2 = () => {
  const [unifiedConfig, setUnifiedConfig] = useState({ models: [] });
  const [addModelDialog, setAddModelDialog] = useState(EMPTY_MODEL_DIALOG);
  const [editModelDialog, setEditModelDialog] = useState({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
  const [minVersionModalVisible, setMinVersionModalVisible] = useState(false);
  const [minVersion, setMinVersion] = useState('');
  const [minVersionInput, setMinVersionInput] = useState('');

  // Shared with video — one app-version bump turns both unifiedV2 configs on.
  const { data: minVersionData } = useQuery(getUnifiedV2MinVersion);
  const [updateMinVersionMutation] = useMutation(updateUnifiedV2MinVersion);

  // Shared duration/resolution/ratio key catalog (managed from the "Standards" tab).
  const { data: standardsData } = useQuery(getCatalogueStandards);
  const resolutionStandards = standardsData?.getCatalogueStandards?.resolutions || [];
  const ratioStandards = standardsData?.getCatalogueStandards?.ratios || [];

  useEffect(() => {
    if (minVersionData?.getUnifiedV2MinVersion !== undefined) {
      const v = minVersionData.getUnifiedV2MinVersion ?? '';
      setMinVersion(v);
      setMinVersionInput(v);
    }
  }, [minVersionData]);

  const handleSaveMinVersion = async () => {
    try {
      await updateMinVersionMutation({ variables: { version: minVersionInput } });
      setMinVersion(minVersionInput);
      setMinVersionModalVisible(false);
      message.success('Minimum unified V2 config version updated.');
    } catch (err) {
      message.error('Failed to update: ' + err.message);
    }
  };

  const modelOrderRef = useRef(
    (() => {
      try { return JSON.parse(localStorage.getItem('imageUnifiedV2ModelOrder')) || null; } catch { return null; }
    })()
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { data, loading, refetch } = useQuery(getModelListImageUnifiedV2);
  const [updateMutation] = useMutation(updateCreditConfigImageUnifiedV2);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const saveOrder = (ids) => {
    modelOrderRef.current = ids;
    try { localStorage.setItem('imageUnifiedV2ModelOrder', JSON.stringify(ids)); } catch {}
  };

  const applyOrder = (models) => {
    const order = modelOrderRef.current;
    if (!order || !order.length) return models;
    return [...models].sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
    });
  };

  // Older saved data used "label" where the field is now "name". Normalize that away here
  // so it stops being resaved. short_name backfills from name/value for rows saved before
  // that field existed. `key` is preserved as-is (undefined for legacy rows pre-standards).
  const stripRowDefaultFlag = (rows) =>
    (rows || []).map(({ key, value, name, label, short_name }) => {
      const rowName = name ?? label ?? '';
      return { key, value, name: rowName, short_name: short_name ?? rowName ?? value ?? '' };
    });

  // Crop ratios also carry aspect_ratio_x/y (used by mobile for cropping). Backfill them
  // from the value string for older rows saved before this field existed.
  const parseRatioXY = (value) => {
    const m = /^(\d+)\s*:\s*(\d+)$/.exec(String(value || '').trim());
    return m ? { x: Number(m[1]), y: Number(m[2]) } : { x: null, y: null };
  };

  const normalizeCropRatios = (rows) =>
    (rows || []).map(({ key, value, name, label, short_name, aspect_ratio_x, aspect_ratio_y }) => {
      const rowName = name ?? label ?? '';
      const rowShortName = short_name ?? rowName ?? value ?? '';
      if (aspect_ratio_x != null && aspect_ratio_y != null) {
        return { key, value, name: rowName, short_name: rowShortName, aspect_ratio_x, aspect_ratio_y };
      }
      const { x, y } = parseRatioXY(value);
      return { key, value, name: rowName, short_name: rowShortName, aspect_ratio_x: x, aspect_ratio_y: y };
    });

  // Older saved data used "label" where the resolution field is now "name".
  const normalizeResolutions = (resolutions) =>
    (resolutions || []).map((r) => ({
      ...r,
      resolution: r.resolution
        ? { key: r.resolution.key, value: r.resolution.value, name: r.resolution.name ?? r.resolution.label ?? '' }
        : r.resolution,
    }));

  // Default selections used to be stored as the row's `value` (provider-specific raw
  // parameter); they're now stored/compared as `key` (stable standard identity). Migrate
  // any old value-based default found in saved data by matching it against the row's
  // `value` and swapping in that row's `key` — so it resaves in the new format instead of
  // silently going stale.
  const migrateDefaultToKey = (rows, oldVal) => {
    if (oldVal == null) return oldVal;
    const match = (rows || []).find((r) => r.value === oldVal);
    return match ? match.key : oldVal;
  };

  useEffect(() => {
    if (data?.getModelListImageUnifiedV2) {
      const raw = JSON.parse(JSON.stringify(data.getModelListImageUnifiedV2));
      const models = (raw.models || []).map((m) => {
        const cropRatios = normalizeCropRatios(m.cropRatios);
        const textToImageRatios = stripRowDefaultFlag(m.textToImageRatios);
        const imgToImageRatios = stripRowDefaultFlag(m.imgToImageRatios);
        const resolutions = normalizeResolutions(m.resolutions);
        const resolutionRowsForMigration = (resolutions || []).map((r) => ({ key: r.resolution?.key, value: r.resolution?.value }));
        const dv = m.default_values ?? {};

        return {
          ...m,
          description: m.description ?? '',
          is_default: m.is_default ?? false,
          is_free_crop_image: m.is_free_crop_image ?? false,
          cropRatios,
          textToImageRatios,
          imgToImageRatios,
          resolutions,
          has_resolution: m.has_resolution ?? (Array.isArray(m.resolutions) && m.resolutions.length > 0),
          default_values: {
            ...dv,
            cropAspectRatio: migrateDefaultToKey(cropRatios, dv.cropAspectRatio),
            textToImageAspectRatio: migrateDefaultToKey(textToImageRatios, dv.textToImageAspectRatio),
            imgToImageAspectRatio: migrateDefaultToKey(imgToImageRatios, dv.imgToImageAspectRatio),
            resolution: migrateDefaultToKey(resolutionRowsForMigration, dv.resolution),
          },
        };
      });
      setUnifiedConfig({ models: applyOrder(models) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleModelDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUnifiedConfig((prev) => {
      const oldIdx = prev.models.findIndex((m) => m.id === active.id);
      const newIdx = prev.models.findIndex((m) => m.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const reordered = arrayMove(prev.models, oldIdx, newIdx);
      saveOrder(reordered.map((m) => m.id));
      return { models: reordered };
    });
  };

  // ---- Icon upload helper ----

  const uploadIcon = async (iconFile) => {
    const { data: signedUrlData } = await getSignedUrlMutation({
      variables: { fileName: iconFile.name },
    });
    const uploadResult = await uploadToR2(
      iconFile,
      signedUrlData.getSignedUrl.url,
      signedUrlData.getSignedUrl.contentType
    );
    if (!uploadResult.success) throw new Error(uploadResult.message);
    const { data: objectUrlData } = await getObjectUrlMutation({
      variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' },
    });
    return objectUrlData.getObjectUrl;
  };

  // ---- CRUD: Models ----

  const countDefaultModels = () =>
    (unifiedConfig.models || []).filter((m) => m.is_default === true).length;

  // Resolution rows (key/value/name/actualCost) from the Add/Edit Model form are
  // reconciled into the `resolutions` array here. Existing `cost` (computed
  // server-side from actual_cost on save) is preserved by matching on key,
  // so re-saving the form doesn't wipe out costs that were already recalculated.
  const buildResolutionsFromRows = (rows, existingResolutions) => {
    return (rows || [])
      .filter((row) => row.key && row.value)
      .map((row) => {
        const existing = (existingResolutions || []).find((r) => r.resolution?.key === row.key);
        return {
          resolution: { key: row.key, value: row.value, name: row.name },
          actual_cost: row.actualCost ?? 0,
          cost: existing?.cost ?? 0,
        };
      });
  };

  const resolutionsToRows = (resolutions) =>
    (resolutions || []).map((r) => ({
      key: r.resolution?.key || '',
      value: r.resolution?.value || '',
      name: r.resolution?.name || '',
      actualCost: r.actual_cost ?? null,
    }));

  const handleAddModel = async () => {
    const d = addModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    if (d.isDefault && countDefaultModels() >= 2) {
      message.error('Cannot mark more than 2 models as default. Please unmark another default model first.');
      return;
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      const newModel = {
        id: d.modelId,
        name: d.modelName,
        description: d.modelDescription || '',
        badge: d.badge || '',
        icon: iconUrl || '',
        cost_preview: d.costPreview || 1,
        maxCharacterLimit: d.maxCharacterLimit || undefined,
        is_default: d.isDefault,
        is_free_crop_image: d.isFreeCropImage,
        cropRatios: [],
        textToImageRatios: [],
        imgToImageRatios: [],
        has_resolution: d.hasResolutionTiers,
        resolutions: d.hasResolutionTiers ? buildResolutionsFromRows(d.resolutionRows, []) : [],
        actual_cost: d.hasResolutionTiers ? 0 : (d.actualCost ?? 0),
        cost: 0,
      };

      setUnifiedConfig((prev) => ({ models: [...prev.models, newModel] }));
      setAddModelDialog(EMPTY_MODEL_DIALOG);
      message.success('Model added');
    } catch (err) {
      message.error('Failed to add model: ' + err.message);
    }
  };

  const handleEditModel = async () => {
    const d = editModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    if (d.isDefault) {
      const currentModel = unifiedConfig.models[d.modelIndex];
      if (!currentModel?.is_default && countDefaultModels() >= 2) {
        message.error('Cannot mark more than 2 models as default. Please unmark another default model first.');
        return;
      }
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      setUnifiedConfig((prev) => {
        const newModels = JSON.parse(JSON.stringify(prev.models));
        const model = newModels[d.modelIndex];
        newModels[d.modelIndex] = {
          ...model,
          id: d.modelId,
          name: d.modelName,
          description: d.modelDescription || '',
          badge: d.badge || '',
          icon: iconUrl || '',
          cost_preview: d.costPreview || 1,
          maxCharacterLimit: d.maxCharacterLimit || undefined,
          is_default: d.isDefault,
          is_free_crop_image: d.isFreeCropImage,
          has_resolution: d.hasResolutionTiers,
          resolutions: d.hasResolutionTiers
            ? buildResolutionsFromRows(d.resolutionRows, model.resolutions || [])
            : [],
          actual_cost: d.hasResolutionTiers ? (model.actual_cost ?? 0) : (d.actualCost ?? 0),
        };
        return { models: newModels };
      });

      setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
      message.success('Model updated');
    } catch (err) {
      message.error('Failed to update model: ' + err.message);
    }
  };

  const handleRemoveModel = (modelIndex) => {
    setUnifiedConfig((prev) => ({
      models: prev.models.filter((_, i) => i !== modelIndex),
    }));
  };

  // ---- CRUD: ratio lists (standards-backed, validation-only, never priced) ----

  const setRatioList = (modelIndex, field, rows) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex][field] = rows;
      return { models: nm };
    });
  };

  // ---- CRUD: Pricing ----
  // Cost is either:
  //  - flat, directly on the model root (model.actual_cost / model.cost), when
  //    `resolutions` is empty — resolution is not required at generation time; or
  //  - per-resolution (resolutions[].actual_cost / .cost), when the model has
  //    one or more resolution rows — resolution becomes required.

  const hasResolutionTiers = (model) =>
    model.has_resolution ?? (Array.isArray(model.resolutions) && model.resolutions.length > 0);

  const addResolutionRowFromStandard = (modelIndex, standard) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].resolutions = [
        ...(nm[modelIndex].resolutions || []),
        { resolution: { key: standard.key, value: standard.key, name: standard.label }, actual_cost: 0, cost: 0 },
      ];
      return { models: nm };
    });
  };

  const removeResolutionRowByKey = (modelIndex, key) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].resolutions = (nm[modelIndex].resolutions || []).filter((r) => r.resolution?.key !== key);
      return { models: nm };
    });
  };

  const updateResolutionField = (modelIndex, resIndex, field, value) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const res = nm[modelIndex]?.resolutions?.[resIndex];
      if (!res) return prev;
      if (field === 'value') {
        res.resolution.value = value;
      } else if (field === 'actual_cost') {
        res.actual_cost = value ?? 0;
      }
      return { models: nm };
    });
  };

  const assignLegacyResolutionKey = (modelIndex, resIndex, standard) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const res = nm[modelIndex]?.resolutions?.[resIndex];
      if (!res) return prev;
      res.resolution = { key: standard.key, value: res.resolution?.value || standard.key, name: standard.label };
      return { models: nm };
    });
  };

  const removeResolutionRow = (modelIndex, resIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].resolutions.splice(resIndex, 1);
      return { models: nm };
    });
  };

  const updateRootActualCost = (modelIndex, value) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].actual_cost = value ?? 0;
      return { models: nm };
    });
  };

  // Default selections live in a single model.default_values object
  // ({ resolution, cropAspectRatio, textToImageAspectRatio, imgToImageAspectRatio })
  // rather than being spread across is_default flags on individual rows.
  const setDefaultValue = (modelIndex, field, value) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].default_values = { ...(nm[modelIndex].default_values || {}), [field]: value };
      return { models: nm };
    });
  };

  const setDefaultResolution = (modelIndex, resValue) => setDefaultValue(modelIndex, 'resolution', resValue);
  // Crop / text-to-image / image-to-image ratios are independent lists, so each gets its own default field.
  const setDefaultCropRatio = (modelIndex, ratioValue) => setDefaultValue(modelIndex, 'cropAspectRatio', ratioValue);
  const setDefaultTextToImageRatio = (modelIndex, ratioValue) => setDefaultValue(modelIndex, 'textToImageAspectRatio', ratioValue);
  const setDefaultImgToImageRatio = (modelIndex, ratioValue) => setDefaultValue(modelIndex, 'imgToImageAspectRatio', ratioValue);

  // ---- Save ----

  const handleSave = async () => {
    try {
      saveOrder(unifiedConfig.models.map((m) => m.id));
      await updateMutation({ variables: { creditJson: unifiedConfig } });
      message.success('Saved. Recalculating costs...');
      const refreshed = await refetch();
      if (refreshed?.data?.getModelListImageUnifiedV2) {
        const raw = JSON.parse(JSON.stringify(refreshed.data.getModelListImageUnifiedV2));
        setUnifiedConfig({ models: applyOrder(raw.models || []) });
      }
      message.success('Image Unified V2 configuration saved & costs recalculated.');
    } catch (err) {
      message.error('Save failed: ' + err.message);
    }
  };

  // ---- Shared model form (Add & Edit) ----
  // Ratios and pricing are edited inline on the model card, not in this form —
  // this only covers model identity/metadata.

  const renderModelForm = (dialogState, setDialogState) => (
    <Form layout="vertical" style={{ marginTop: 16 }}>
      <Form.Item label="Model ID" required>
        <Input
          value={dialogState.modelId}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelId: e.target.value }))}
          placeholder="e.g., seedream-4"
        />
      </Form.Item>
      <Form.Item label="Model Name" required>
        <Input
          value={dialogState.modelName}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelName: e.target.value }))}
          placeholder="e.g., Seedream 4"
        />
      </Form.Item>
      <Form.Item label="Badge">
        <Input
          value={dialogState.badge}
          onChange={(e) => setDialogState((prev) => ({ ...prev, badge: e.target.value }))}
          placeholder="e.g., New, Popular (optional)"
        />
      </Form.Item>
      <Form.Item label="Model Icon">
        {dialogState.icon && (
          <div style={{ marginBottom: 8 }}>
            <img
              src={dialogState.icon}
              alt="Current icon"
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Current icon</div>
          </div>
        )}
        <Upload
          accept="image/*,image/svg+xml"
          maxCount={1}
          beforeUpload={(file) => {
            const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml';
            if (!isImage) { message.error('Only image files allowed!'); return Upload.LIST_IGNORE; }
            if (file.size / 1024 / 1024 >= 5) { message.error('Max 5MB'); return Upload.LIST_IGNORE; }
            setDialogState((prev) => ({ ...prev, iconFile: file }));
            return false;
          }}
          onRemove={() => setDialogState((prev) => ({ ...prev, iconFile: null }))}
        >
          <Button icon={<UploadOutlined />}>
            {dialogState.icon ? 'Change Icon' : 'Upload Icon (Optional)'}
          </Button>
        </Upload>
      </Form.Item>
      <Form.Item label="Description">
        <Input.TextArea
          value={dialogState.modelDescription}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelDescription: e.target.value }))}
          placeholder="Brief description of the model (optional)"
          rows={2}
        />
      </Form.Item>
      <Form.Item label="Cost Preview">
        <Slider
          min={1}
          max={5}
          value={dialogState.costPreview || 1}
          onChange={(val) => setDialogState((prev) => ({ ...prev, costPreview: val }))}
          marks={{ 1: '$', 2: '$$', 3: '$$$', 4: '$$$$', 5: '$$$$$' }}
        />
      </Form.Item>
      <Form.Item label="Max Prompt Character Limit" help="Leave empty for no limit. Rejects the generation before it's queued if the prompt is longer.">
        <InputNumber
          min={1}
          style={{ width: 160 }}
          value={dialogState.maxCharacterLimit}
          onChange={(val) => setDialogState((prev) => ({ ...prev, maxCharacterLimit: val }))}
        />
      </Form.Item>
      <Divider />
      <Form.Item label="Is Free Crop Image">
        <Switch
          checked={dialogState.isFreeCropImage}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isFreeCropImage: val }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Whether image input uses free crop. The Crop Ratios list (below, on the model card) is UI-only and not sent to the backend as a generation parameter.
        </p>
      </Form.Item>

      <Divider />
      <Form.Item
        label="Supports Resolution Tiers"
        extra="If enabled, cost depends on the requested resolution and resolution becomes required at generation time. If disabled, every generation costs the same flat amount."
      >
        <Switch
          checked={dialogState.hasResolutionTiers}
          onChange={(val) => setDialogState((prev) => ({ ...prev, hasResolutionTiers: val }))}
          checkedChildren="Per-resolution"
          unCheckedChildren="Flat"
        />
      </Form.Item>

      {dialogState.hasResolutionTiers ? (
        <Form.Item label="Resolutions & Pricing ($/image)">
          <StandardChips
            standards={resolutionStandards}
            existingKeys={new Set((dialogState.resolutionRows || []).map((r) => r.key))}
            color="#52c41a"
            onAdd={(s) => setDialogState((prev) => ({
              ...prev,
              resolutionRows: [...(prev.resolutionRows || []), { key: s.key, value: s.key, name: s.label, actualCost: null }],
            }))}
            onRemove={(key) => setDialogState((prev) => ({
              ...prev,
              resolutionRows: (prev.resolutionRows || []).filter((r) => r.key !== key),
            }))}
          />
          {(dialogState.resolutionRows || []).length > 0 && (
            <Table
              dataSource={dialogState.resolutionRows}
              rowKey={(_, i) => i}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Key',
                  key: 'key',
                  width: 110,
                  render: (_, row, i) => row.key ? <Tag>{row.key}</Tag> : (
                    <LegacyKeyPicker
                      standards={resolutionStandards.filter((s) => !(dialogState.resolutionRows || []).some((r) => r.key === s.key))}
                      onPick={(key) => setDialogState((prev) => {
                        const s = resolutionStandards.find((x) => x.key === key);
                        if (!s) return prev;
                        const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                        rows[i] = { key: s.key, value: rows[i].value || s.key, name: s.label, actualCost: rows[i].actualCost };
                        return { ...prev, resolutionRows: rows };
                      })}
                    />
                  ),
                },
                {
                  title: 'Value',
                  key: 'value',
                  width: 140,
                  render: (_, row, i) => (
                    <Input
                      size="small"
                      placeholder="e.g. 1024x1024"
                      value={row.value}
                      onChange={(e) => setDialogState((prev) => {
                        const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                        rows[i].value = e.target.value;
                        return { ...prev, resolutionRows: rows };
                      })}
                    />
                  ),
                },
                {
                  title: 'Name',
                  key: 'name',
                  width: 140,
                  render: (_, row) => <span style={{ color: '#666' }}>{row.name}</span>,
                },
                {
                  title: '$/image',
                  key: 'actualCost',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.001} style={{ width: '100%' }}
                      value={row.actualCost}
                      onChange={(val) => setDialogState((prev) => {
                        const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                        rows[i] = { ...rows[i], actualCost: val };
                        return { ...prev, resolutionRows: rows };
                      })}
                    />
                  ),
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 60,
                  render: (_, __, i) => (
                    <Button
                      danger size="small" type="text" icon={<DeleteOutlined />}
                      onClick={() => setDialogState((prev) => ({
                        ...prev,
                        resolutionRows: prev.resolutionRows.filter((_, idx) => idx !== i),
                      }))}
                    />
                  ),
                },
              ]}
            />
          )}
        </Form.Item>
      ) : (
        <Form.Item label="Actual Cost ($/image)" extra="Flat cost charged on every generation, regardless of resolution.">
          <InputNumber
            min={0}
            step={0.001}
            style={{ width: 200 }}
            value={dialogState.actualCost}
            onChange={(val) => setDialogState((prev) => ({ ...prev, actualCost: val ?? 0 }))}
            prefix="$"
          />
        </Form.Item>
      )}

      <Form.Item label="Mark as Default Model">
        <Switch
          checked={dialogState.isDefault}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isDefault: val }))}
          checkedChildren="Default"
          unCheckedChildren="Not Default"
        />
      </Form.Item>
    </Form>
  );

  // ---- Render ----

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1600px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={2} style={{ color: 'black', margin: 0 }}>
          Unified Image Credit Configuration V2
        </Title>
        <Button
          icon={<SettingOutlined />}
          onClick={() => {
            setMinVersionInput(minVersion);
            setMinVersionModalVisible(true);
          }}
        >
          Set Minimum App Version{minVersion ? ` (${minVersion})` : ''}
        </Button>
      </div>

      <Modal
        title="Set Minimum App Version for Unified V2 Configs"
        open={minVersionModalVisible}
        onOk={handleSaveMinVersion}
        onCancel={() => setMinVersionModalVisible(false)}
        okText="Save"
        okButtonProps={{ disabled: minVersionInput === minVersion }}
        destroyOnClose
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Minimum App Version"
            extra="Shared with video — users on this version or above receive BOTH the video and image unified V2 credit configs and are expected to send raw ratio/resolution values. Also editable from the video Unified V2 tab."
          >
            <Input
              value={minVersionInput}
              onChange={(e) => setMinVersionInput(e.target.value)}
              placeholder="e.g., 4.0.0"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Models card */}
      <Card
        title={<span style={{ fontSize: 18, fontWeight: 600 }}>Models</span>}
        extra={
          <Space>
            <Button icon={<SaveOutlined />} onClick={handleSave}>Save Changes</Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAddModelDialog({ ...EMPTY_MODEL_DIALOG, visible: true })}
            >
              Add Model
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {!unifiedConfig.models || unifiedConfig.models.length === 0 ? (
          <Empty description="No models configured yet" style={{ padding: '40px' }} />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModelDragEnd}>
            <SortableContext items={unifiedConfig.models.map((m) => m.id)} strategy={verticalListSortingStrategy}>
              {unifiedConfig.models.map((model, modelIndex) => (
                <SortableModelWrapper key={model.id} id={model.id}>
                  <Collapse
                    style={{ flex: 1, border: 'none', borderRadius: 0 }}
                    expandIconPosition="end"
                    items={[{
                      key: '0',
                      label: (
                        <Space align="start">
                          {model.icon ? (
                            <img
                              src={model.icon}
                              alt={model.name}
                              style={{ width: 48, height: 48, borderRadius: 4, objectFit: 'cover', backgroundColor: '#f0f0f0', border: '1px solid #d9d9d9' }}
                            />
                          ) : (
                            <Avatar size={48} shape="square" style={{ marginTop: 4, backgroundColor: '#f0f0f0' }} icon={<SettingOutlined />} />
                          )}
                          <div style={{ flex: 1 }}>
                            <Space wrap>
                              <Badge status="processing" color="#52c41a" />
                              <strong>{model.name}</strong>
                              <Tag color="purple">{model.id}</Tag>
                              {model.badge && <Tag color="magenta">{model.badge}</Tag>}
                              {model.is_default && <Tag color="gold">Default Model</Tag>}
                              {model.is_free_crop_image && <Tag color="green">Free Crop</Tag>}
                              {hasResolutionTiers(model) ? (
                                <Tag color="cyan">Resolution Tiers</Tag>
                              ) : (
                                <Tag color="default">Flat Price</Tag>
                              )}
                              {model.cost_preview && <Tag color="cyan">{'$'.repeat(model.cost_preview)}</Tag>}
                            </Space>
                            {model.description && (
                              <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                                {model.description}
                              </div>
                            )}
                          </div>
                        </Space>
                      ),
                      extra: (
                        <Space>
                          <Tooltip title="Edit Model Settings">
                            <Button
                              type="default"
                              size="small"
                              icon={<SettingOutlined />}
                              onClick={() => {
                                setEditModelDialog({
                                  visible: true,
                                  modelIndex,
                                  modelId: model.id,
                                  modelName: model.name,
                                  modelDescription: model.description || '',
                                  badge: model.badge || '',
                                  icon: model.icon || '',
                                  iconFile: null,
                                  costPreview: model.cost_preview || 1,
                                  maxCharacterLimit: model.maxCharacterLimit ?? null,
                                  isDefault: model.is_default === true,
                                  isFreeCropImage: model.is_free_crop_image ?? false,
                                  hasResolutionTiers: hasResolutionTiers(model),
                                  resolutionRows: resolutionsToRows(model.resolutions),
                                  actualCost: model.actual_cost ?? 0,
                                });
                              }}
                            >
                              Edit
                            </Button>
                          </Tooltip>
                          <Popconfirm
                            title="Remove Model"
                            description={`Remove ${model.name}?`}
                            onConfirm={() => handleRemoveModel(modelIndex)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                          </Popconfirm>
                        </Space>
                      ),
                      children: (
                        <>
                          <Collapse
                            style={{ marginBottom: 8 }}
                            items={[{
                              key: 'ratios',
                              label: <span style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1 }}>RATIOS</span>,
                              children: (
                                <>
                                  <RatioListEditor
                                    title="CROP RATIOS (UI-only, mobile input crop)"
                                    rows={model.cropRatios}
                                    onChange={(rows) => setRatioList(modelIndex, 'cropRatios', rows)}
                                    defaultValue={model.default_values?.cropAspectRatio}
                                    onSetDefault={(value) => setDefaultCropRatio(modelIndex, value)}
                                    withAspectXY
                                    standardsList={ratioStandards}
                                  />
                                  <RatioListEditor
                                    title="TEXT-TO-IMAGE RATIOS"
                                    rows={model.textToImageRatios}
                                    onChange={(rows) => setRatioList(modelIndex, 'textToImageRatios', rows)}
                                    defaultValue={model.default_values?.textToImageAspectRatio}
                                    onSetDefault={(value) => setDefaultTextToImageRatio(modelIndex, value)}
                                    standardsList={ratioStandards}
                                  />
                                  <RatioListEditor
                                    title="IMAGE-TO-IMAGE RATIOS"
                                    rows={model.imgToImageRatios}
                                    onChange={(rows) => setRatioList(modelIndex, 'imgToImageRatios', rows)}
                                    defaultValue={model.default_values?.imgToImageAspectRatio}
                                    onSetDefault={(value) => setDefaultImgToImageRatio(modelIndex, value)}
                                    standardsList={ratioStandards}
                                  />
                                </>
                              ),
                            }]}
                          />

                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>
                              PRICING
                            </div>

                            {hasResolutionTiers(model) ? (
                              <>
                                <StandardChips
                                  standards={resolutionStandards}
                                  existingKeys={new Set((model.resolutions || []).map((r) => r.resolution?.key))}
                                  color="#52c41a"
                                  onAdd={(s) => addResolutionRowFromStandard(modelIndex, s)}
                                  onRemove={(key) => removeResolutionRowByKey(modelIndex, key)}
                                />
                                <Table
                                  dataSource={model.resolutions}
                                  rowKey={(_, i) => i}
                                  pagination={false}
                                  size="small"
                                  columns={[
                                    {
                                      title: 'Default',
                                      key: 'is_default',
                                      width: 50,
                                      render: (_, row) => {
                                        const isDefaultRes = model.default_values?.resolution != null && row.resolution?.key === model.default_values?.resolution;
                                        return (
                                          <Tooltip title={isDefaultRes ? 'Default' : 'Set as default'}>
                                            <Button
                                              type="text"
                                              size="small"
                                              icon={isDefaultRes ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                                              onClick={() => setDefaultResolution(modelIndex, row.resolution?.key)}
                                            />
                                          </Tooltip>
                                        );
                                      },
                                    },
                                    {
                                      title: 'Key',
                                      key: 'key',
                                      width: 110,
                                      render: (_, row, i) => row.resolution?.key ? <Tag>{row.resolution.key}</Tag> : (
                                        <LegacyKeyPicker
                                          standards={resolutionStandards.filter((s) => !(model.resolutions || []).some((r) => r.resolution?.key === s.key))}
                                          onPick={(key) => {
                                            const s = resolutionStandards.find((x) => x.key === key);
                                            if (s) assignLegacyResolutionKey(modelIndex, i, s);
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      title: 'Value',
                                      key: 'value',
                                      width: 140,
                                      render: (_, row, i) => (
                                        <Input
                                          size="small"
                                          placeholder="e.g. 1024x1024"
                                          value={row.resolution?.value}
                                          onChange={(e) => updateResolutionField(modelIndex, i, 'value', e.target.value)}
                                        />
                                      ),
                                    },
                                    {
                                      title: 'Name',
                                      key: 'name',
                                      width: 140,
                                      render: (_, row) => <span style={{ color: '#666' }}>{row.resolution?.name}</span>,
                                    },
                                    {
                                      title: 'Actual Cost ($/image)',
                                      key: 'actual_cost',
                                      width: 170,
                                      render: (_, row, i) => (
                                        <InputNumber
                                          size="small"
                                          min={0}
                                          step={0.001}
                                          style={{ width: '100%' }}
                                          value={row.actual_cost}
                                          onChange={(val) => updateResolutionField(modelIndex, i, 'actual_cost', val)}
                                          prefix="$"
                                        />
                                      ),
                                    },
                                    {
                                      title: 'Credit Cost',
                                      key: 'cost',
                                      width: 130,
                                      render: (_, row) => (
                                        <InputNumber value={row.cost ?? 0} disabled min={0} style={{ width: '100%' }} prefix={<DollarOutlined />} />
                                      ),
                                    },
                                    {
                                      title: 'Action',
                                      key: 'action',
                                      width: 60,
                                      render: (_, __, i) => (
                                        <Button
                                          danger size="small" type="text" icon={<DeleteOutlined />}
                                          onClick={() => removeResolutionRow(modelIndex, i)}
                                        />
                                      ),
                                    },
                                  ]}
                                />
                              </>
                            ) : (
                              <Space>
                                <span style={{ width: 140, display: 'inline-block' }}>Actual Cost ($/image)</span>
                                <InputNumber
                                  min={0}
                                  step={0.001}
                                  value={model.actual_cost ?? 0}
                                  onChange={(val) => updateRootActualCost(modelIndex, val)}
                                  prefix="$"
                                />
                                <span style={{ width: 90, display: 'inline-block', marginLeft: 16 }}>Credit Cost</span>
                                <InputNumber value={model.cost ?? 0} disabled min={0} prefix={<DollarOutlined />} />
                              </Space>
                            )}
                          </div>
                        </>
                      ),
                    }]}
                  />
                </SortableModelWrapper>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {/* Add Model Modal */}
      <Modal
        title="Add Model"
        open={addModelDialog.visible}
        onOk={handleAddModel}
        onCancel={() => setAddModelDialog(EMPTY_MODEL_DIALOG)}
        width={600}
        destroyOnClose
      >
        {renderModelForm(addModelDialog, setAddModelDialog)}
      </Modal>

      {/* Edit Model Modal */}
      <Modal
        title="Edit Model"
        open={editModelDialog.visible}
        onOk={handleEditModel}
        onCancel={() => setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 })}
        width={600}
        destroyOnClose
      >
        {renderModelForm(editModelDialog, setEditModelDialog)}
      </Modal>
    </div>
  );
};

export default ImageUnifiedCreditConfigV2;
